import { useMutation, useQuery } from "@tanstack/react-query";
import {
  deleteCoachingNote,
  getCoachingNotes,
  regenerateCoachingNoteThumbnail,
  updateCoachingNote,
  type ApiError,
} from "@/lib/api";
import type { CoachingVisibility } from "@/lib/types";
import { invalidateScope, useScopedKey } from "@/lib/queryClient";
import { useScopeStore } from "@/stores/scope";
import { settleBulk, type BulkResult } from "./bulkOps";

/**
 * Coach > Notes list-page data hooks (M6a).
 *
 * `useCoachNotesList` is the FULL team-notes list — distinct from
 * M5's `useCoachNotes(matchId)`, which narrows to one match for
 * the Coach Review timeline rail. The Notes list page shows every
 * note (video + observation) for the active scope, so it calls
 * `getCoachingNotes()` with no `matchId`.
 *
 * The cache key is `useScopedKey("coaching-notes", {match_id:
 * null})` — the SAME key the M5 timeline rail uses for its
 * "no match selected" case AND the same key the Roster page's
 * notes-per-player KPI reads. One cache bucket, shared: a note
 * save/delete `invalidateScope`s it and all three surfaces
 * refetch.
 *
 * `useCoachNoteMutations` bundles the list-page write paths:
 * delete a note, regenerate a video note's thumbnail. (Create +
 * edit go through Coach Review's `useSaveCoachingNote` — the
 * Notes list routes INTO Coach Review for those.)
 */

export function useCoachNotesList(options?: { enabled?: boolean }) {
  const teamId = useScopeStore((s) => s.teamId);
  return useQuery({
    queryKey: useScopedKey("coaching-notes", { match_id: null }),
    queryFn: () => getCoachingNotes(),
    enabled: !!teamId && (options?.enabled ?? true),
  });
}

export type CoachNoteMutations = {
  deleteNote: (id: number) => Promise<{ ok: true }>;
  regenerateThumbnail: (
    id: number,
  ) => Promise<{ ok: true; generated: boolean }>;
  /** A6: bulk visibility change. PATCHes each id with only
   * `{ visibility }` (the route's `exclude_unset` preserves every
   * other field), then invalidates the scope ONCE. */
  bulkUpdateVisibility: (
    ids: number[],
    visibility: CoachingVisibility,
  ) => Promise<BulkResult>;
  /** A6: bulk delete. DELETEs each id, then invalidates ONCE. */
  bulkDelete: (ids: number[]) => Promise<BulkResult>;
  isPending: boolean;
  error: ApiError | null;
};

export function useCoachNoteMutations(): CoachNoteMutations {
  const remove = useMutation({
    mutationFn: deleteCoachingNote,
    onSuccess: () => invalidateScope(),
  });
  const regen = useMutation({
    mutationFn: regenerateCoachingNoteThumbnail,
    // A regenerated JPEG doesn't change the note row — no
    // `invalidateScope` needed; the `useThumbnail` cache is
    // invalidated separately by the caller via
    // `invalidateThumbnail("note", id)`.
  });
  // A6: the bulk paths fan out N idempotent per-id calls (library
  // volumes are tens of rows) and invalidate once at the end — N
  // invalidations would thrash the list cache mid-batch.
  const bulkVisibility = useMutation({
    mutationFn: ({
      ids,
      visibility,
    }: {
      ids: number[];
      visibility: CoachingVisibility;
    }) =>
      settleBulk(ids, (id) => updateCoachingNote(id, { visibility })),
    onSuccess: () => invalidateScope(),
  });
  const bulkRemove = useMutation({
    mutationFn: (ids: number[]) =>
      settleBulk(ids, (id) => deleteCoachingNote(id)),
    onSuccess: () => invalidateScope(),
  });

  const all = [remove, regen, bulkVisibility, bulkRemove];
  return {
    deleteNote: (id) => remove.mutateAsync(id),
    regenerateThumbnail: (id) => regen.mutateAsync(id),
    bulkUpdateVisibility: (ids, visibility) =>
      bulkVisibility.mutateAsync({ ids, visibility }),
    bulkDelete: (ids) => bulkRemove.mutateAsync(ids),
    isPending: all.some((m) => m.isPending),
    error: (all.find((m) => m.error)?.error as ApiError | undefined) ?? null,
  };
}
